/**
 * StashThumbnail - Core thumbnail component for displaying stash items
 *
 * Supports multiple display modes:
 * - Standard: Clickable thumbnail with optional title overlay
 * - Edit mode: Multi-select with checkbox
 * - Custom overlays: Via `overlay` prop for badges, indicators, etc.
 *
 * The `overlay` prop accepts any ReactNode and renders it absolutely positioned
 * within the thumbnail container. Use with ThumbnailOverlayBadge for consistent styling.
 */

import { memo, useCallback, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, EyeOff, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD_TITLE, META } from "@/lib/typography";
import { PlatformIcon } from "@/components/shared/PlatformIcon";
import { ThumbnailOverlayBadge } from "./ThumbnailOverlayBadge";
import { isPostRestricted } from "@/utils/stashState";
import { getSensitiveContentSetting, getDisplayPreference } from "@/contexts/PreferencesContext";
import type { PipelinePostItem } from "@/hooks/usePipelineStatus";
import type { Post } from "@/services/posts.service";
import { cardImageUrl, originalToPrefetch, type MediaVariants } from "@/features/stash/mediaVariants";

// Minimal interface for StashThumbnail - compatible with FeedPost
type StashMediaItem = {
  url?: string;
  cdn_url?: string;
  original_url?: string;
  original_instagram_url?: string;
  type?: string;
  is_thumbnail?: boolean;
  is_cover?: boolean;
  variants?: MediaVariants;
};

export interface StashThumbnailData {
  id: string;
  title?: string;
  user_title?: string;
  description?: string;
  visibility?: "private" | "public";
  content?: {
    possibly_sensitive?: boolean;
    content_type?: string;
    media?: StashMediaItem[];
  };
  canonical_url?: string | null;
  sourceUrl?: string | null;
  platform?: string | null;
  source?: string | { platform?: string; type?: string } | null;
  // Pipeline state for pending/processing detection
  pipeline?: Record<string, unknown>;
  enrichment_status?: string;
  needs_enrichment?: boolean;
  // Enrichment response (fallback for title)
  main_enrichment_response?: { title?: string };
  // Unfurl outcome for restricted/private content
  unfurl_status?: Post["unfurl_status"];
  // Map surfaces can highlight every matching pin for a post while hovering.
  matching_entity_ids?: string[];
}

export type ThumbnailWidth = "fixed" | "sm" | "md" | "lg" | "full";
export type ThumbnailRadius = "none" | "lg" | "xl" | "2xl";

export interface StashThumbnailProps {
  post: StashThumbnailData;
  onClick?: (post: StashThumbnailData, e?: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  href?: string; // If provided, renders as Link instead of button
  width?: ThumbnailWidth;
  cornerRadius?: ThumbnailRadius;
  aspectRatio?: string;
  hoverScale?: boolean;
  showTitle?: boolean;
  titleClassName?: string;
  useNaturalAspectRatio?: boolean;
  loading?: "lazy" | "eager";
  className?: string;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (postId: string) => void;
  pipelineData?: PipelinePostItem;
  /**
   * Custom overlay content rendered absolutely within the thumbnail.
   * Use with ThumbnailOverlayBadge for consistent badge styling.
   * Supports multiple badges - wrap in fragment: <>{badge1}{badge2}</>
   */
  overlay?: ReactNode;
  /** Metadata/actions following the card, outside its image and navigation link. */
  footer?: ReactNode;
  /** A known identity whose canonical data could not be read. */
  unavailable?: boolean;
  onRetry?: () => unknown;
  /** Show the platform icon overlay (default true). Set false for map stash cards. */
  showPlatformIcon?: boolean;
  /** Position of the platform icon (default "top-left"). */
  platformIconPosition?: "top-left" | "top-right";
  /** Icon size in px (default 14). Smaller values shrink the badge container too. */
  platformIconSize?: number;
  /** Use the tight PlatformIcon container for compact cards. */
  platformIconCompact?: boolean;
}

const widthClasses: Record<ThumbnailWidth, string> = {
  fixed: "w-[192px]",
  sm: "w-32",
  md: "w-40",
  lg: "w-48",
  full: "w-full",
};

const radiusClasses: Record<ThumbnailRadius, string> = {
  none: "rounded-none",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
};

// The image item a card draws - only images, only cdn_url.
// Priority: generated thumbnail > cover image > any image.
function getCardMediaItem(post: StashThumbnailData, pipelineData?: PipelinePostItem): StashMediaItem | undefined {
  const allMedia = post.content?.media || pipelineData?.content?.media || [];

  const thumbnail = allMedia.find(m => m.is_thumbnail === true && m.type === "image");
  if (thumbnail?.cdn_url) return thumbnail;

  const coverImage = allMedia.find(m => m.is_cover === true && m.type === "image");
  if (coverImage?.cdn_url) return coverImage;

  // Only use cdn_url - no fallbacks to expired Instagram URLs
  return allMedia.find(m => m.type === "image");
}

// A card draws the item's thumb variant when ingest has minted one, else the original.
function getMediaUrl(post: StashThumbnailData, pipelineData?: PipelinePostItem): string | undefined {
  return cardImageUrl(getCardMediaItem(post, pipelineData));
}

/** The image a stash card draws, for other surfaces that preview stashes (Home's All stashes cover). */
export const stashCardImageUrl = (post: StashThumbnailData): string | undefined => getMediaUrl(post);

// Warm the original on press so the detail finds it in cache, as it did when
// cards drew originals.
function prefetchOriginalOnPress(post: StashThumbnailData, pipelineData?: PipelinePostItem) {
  const original = originalToPrefetch(getCardMediaItem(post, pipelineData));
  if (!original || typeof Image === "undefined") return;
  const image = new Image();
  image.src = original;
}

// Get display title - user_title takes priority, then enriched title
function getDisplayTitle(post: StashThumbnailData): string {
  return post.user_title || post.title || post.main_enrichment_response?.title || "";
}

function getSourceLabel(post: StashThumbnailData): string {
  const sourceUrl = post.sourceUrl || post.canonical_url;
  if (sourceUrl) {
    try {
      return new URL(sourceUrl).hostname.replace(/^www\./, "");
    } catch {
      // Fall through to the observed platform.
    }
  }
  const sourcePlatform = typeof post.source === "object" ? post.source?.platform : post.source;
  const platform = post.platform || sourcePlatform;
  return platform && platform !== "unknown" ? platform : "";
}

function getTitleFallbackTextClass(title: string): string {
  const length = title.trim().length;
  if (length <= 36) return "text-ui-card-title leading-snug";
  if (length <= 80) return "text-ui-secondary-copy leading-snug";
  if (length <= 140) return "text-ui-control leading-snug";
  return "text-ui-caption leading-snug";
}

export const StashThumbnail = memo(
  ({
    post,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    href,
    width = "full",
    cornerRadius = "2xl",
    aspectRatio = "aspect-[9/16]",
    hoverScale = true,
    showTitle,
    titleClassName,
    useNaturalAspectRatio = false,
    loading = "lazy",
    className,
    isEditMode = false,
    isSelected = false,
    onToggleSelect,
    pipelineData,
    overlay,
    footer,
    unavailable,
    onRetry,
    showPlatformIcon = true,
    platformIconPosition = "top-left",
    platformIconSize,
    platformIconCompact,
  }: StashThumbnailProps) => {
    // Use display preference if showTitle not explicitly set
    const resolvedShowTitle = showTitle ?? getDisplayPreference("showTitlesOnThumbs");
    const [imgFailed, setImgFailed] = useState(false);
    // A variant is a hint; the original is the truth. If the variant fails to
    // load, the card draws the original before giving up on the image.
    const [variantFailed, setVariantFailed] = useState(false);
    const cardItem = getCardMediaItem(post, pipelineData);
    const variantUrl = getMediaUrl(post, pipelineData);
    const rawMediaUrl = variantFailed ? cardItem?.cdn_url : variantUrl;
    const mediaUrl = imgFailed ? undefined : rawMediaUrl;
    const handleImgError = () => {
      if (!variantFailed && cardItem?.cdn_url && variantUrl !== cardItem.cdn_url) {
        setVariantFailed(true);
        return;
      }
      setImgFailed(true);
    };
    const title = getDisplayTitle(post);
    const sourceLabel = getSourceLabel(post);
    const cardText = title || sourceLabel;
    const platform = post.platform || pipelineData?.source?.platform;
    const isRestricted = isPostRestricted(post);
    const shouldShowTitleFallback = !mediaUrl && !isRestricted && Boolean(cardText);
    const useNaturalTitleFallback = useNaturalAspectRatio && shouldShowTitleFallback;

    // Sensitive content check
    const isSensitive = post.content?.possibly_sensitive === true;
    const showSensitive = getSensitiveContentSetting();
    const shouldBlur = isSensitive && !showSensitive;

    const effectiveEditMode = isEditMode || !!onToggleSelect;

    const handleClick = useCallback(
      (e?: React.MouseEvent) => {
        if (effectiveEditMode && onToggleSelect) {
          e?.preventDefault();
          onToggleSelect(post.id);
        } else if (onClick) {
          onClick(post, e);
        }
      },
      [effectiveEditMode, onToggleSelect, post, onClick],
    );

    // Use Link if href provided, otherwise button
    const Wrapper = href ? Link : "button";
    const linkState = href?.startsWith("/stash/") ? { stash: post } : undefined;
    // Warm the original on press: the detail asks for it a slide later.
    const handlePointerDown = () => prefetchOriginalOnPress(post, pipelineData);
    const wrapperProps = href
      ? { to: href, state: linkState, onClick: effectiveEditMode ? handleClick : undefined, onPointerDown: handlePointerDown }
      : { type: "button" as const, onClick: handleClick, onPointerDown: handlePointerDown };

    const buttonClasses = cn(
      "flex-shrink-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group",
      width === "full" ? "w-full" : widthClasses[width],
      className,
    );

    if (unavailable) return <div data-card-id={post.id} className={cn("relative flex-shrink-0 min-w-0", widthClasses[width], className)}>
      <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-4 pb-4 pt-14">
        {overlay}
        <p className={META}>Stash details unavailable</p>
        <div className="flex flex-wrap gap-2">
          {onRetry && <Button variant="ghost" size="control" onClick={() => void onRetry()}>Try again</Button>}
          {href && <Button asChild variant="ghost" size="control"><Link to={href}>View stash</Link></Button>}
        </div>
      </div>
      {footer && <div className="mt-2">{footer}</div>}
    </div>;

    // A source-less post cannot paint a useful card. Pipeline state never changes
    // this decision and never substitutes a processing card.
    if (!mediaUrl && !cardText && !isRestricted) {
      return null;
    }

    // Masonry vs fixed aspect ratio
    const resolvedAspectRatio =
      useNaturalAspectRatio && (mediaUrl || shouldShowTitleFallback) ? "aspect-auto" : aspectRatio;
    const mediaClassName =
      useNaturalAspectRatio && mediaUrl ? "w-full h-auto object-cover" : "h-full w-full object-cover";

    // Media card
    const card = (
      <Wrapper
        {...wrapperProps}
        data-card-id={footer ? undefined : post.id}
        className={buttonClasses}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <div
          className={cn(
            "relative w-full overflow-hidden",
            resolvedAspectRatio,
            radiusClasses[cornerRadius],
            hoverScale && !isEditMode && " transform-gpu",
          )}
        >
          {/* Selected border overlay — inset so it doesn't shift layout */}
          {isEditMode && isSelected && (
            <div
              className={cn(
                "absolute inset-0 z-10 pointer-events-none border-3 border-white bg-white/20 dark:bg-transparent",
                radiusClasses[cornerRadius],
              )}
            />
          )}

          {/* Edit mode checkbox */}
          {isEditMode && (
            <div className="absolute top-2 right-2 z-20">
              <div
                className={cn(
                  "h-6 w-6 rounded-md border-2 flex items-center justify-center shadow-sm",
                  isSelected ? "bg-white border-white" : "bg-white/80 border-white/60",
                )}
              >
                {isSelected && <Check className="h-4 w-4 text-black" />}
              </div>
            </div>
          )}

          {/* Custom overlay (badges, indicators, etc.) */}
          {overlay}

          {/* Restricted content badge */}
          {isRestricted && <ThumbnailOverlayBadge variant="restricted" position="bottom-right" />}

          {/* Media */}
          {isRestricted && !mediaUrl ? (
            <div className="flex h-full w-full flex-col items-center justify-center bg-muted/80">
              <Lock className="h-8 w-8 text-muted-foreground/60" />
            </div>
          ) : shouldShowTitleFallback ? (
            <div
              className={cn(
                "flex w-full items-center justify-center bg-card px-4",
                useNaturalTitleFallback ? "min-h-[72px] py-4" : "h-full py-5",
              )}
            >
              <h3
                className={cn(
                  "text-center font-medium text-card-foreground break-words whitespace-normal",
                  getTitleFallbackTextClass(title),
                  useNaturalTitleFallback ? "line-clamp-8" : "line-clamp-6",
                  titleClassName,
                )}
              >
                {cardText}
              </h3>
            </div>
          ) : (
            <>
              <img
                src={mediaUrl}
                alt={title || "Thumbnail"}
                className={cn(mediaClassName, shouldBlur && "blur-xl scale-110")}
                loading={loading}
                onError={handleImgError}
              />
              {shouldBlur && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <EyeOff className="h-6 w-6 text-white/70" />
                </div>
              )}
            </>
          )}

          {/* Platform icon */}
          {showPlatformIcon && (
            <PlatformIcon
              canonicalUrl={post.canonical_url}
              sourceUrl={post.sourceUrl}
              platform={platform}
              source={post.source}
              position={platformIconPosition}
              size={platformIconSize}
              compact={platformIconCompact}
            />
          )}

        </div>

        {/* The title sits under the cover, as natively (StashTile), and only when titles
            are on: off, nothing follows the cover. */}
        {resolvedShowTitle && !shouldShowTitleFallback && title && (
          <h3 className={cn(CARD_TITLE, "mt-2 line-clamp-2 break-words text-foreground", titleClassName)}>{title}</h3>
        )}
      </Wrapper>
    );
    return footer ? <div data-card-id={post.id} className={cn("flex-shrink-0 min-w-0", widthClasses[width])}>
      {card}<div className="mt-2">{footer}</div>
    </div> : card;
  },
);

StashThumbnail.displayName = "StashThumbnail";
